import { ReactNode } from 'react';

import { LucideIcon } from 'lucide-react';

import { StatusBadgeType } from '@/modules/common';
import { PatchEntityResult } from '@/modules/common/types/patch-entity-result';

/**
 * Date range passed from footer to tabs during submission
 * Used for conflict detection in Edit/EditChange modes
 */
export interface SubmitDateRange {
  from: Date;
  to?: Date | null;
}

export interface TabConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  hasWarning?: boolean;
  hasError?: boolean;
  /**
   * Lets the tab own vertical scrolling. Use for screens with sticky content
   * that needs a dedicated scroll container rather than the tabs panel.
   */
  fillAvailableHeight?: boolean;
}

export interface FeatureTab<TFormData extends BaseTabFormData = BaseTabFormData> {
  id: string;
  label: string;
  icon: LucideIcon;
  content: ReactNode;
  ref: React.RefObject<FeatureTabRef<TFormData> | null>;
  maxWidth?:
    | 'max-w-sm'
    | 'max-w-md'
    | 'max-w-lg'
    | 'max-w-xl'
    | 'max-w-2xl'
    | 'max-w-3xl'
    | 'max-w-4xl'
    | 'max-w-5xl'
    | 'max-w-6xl'
    | 'max-w-7xl'
    | 'max-w-full';
  /** See {@link TabConfig.fillAvailableHeight}. */
  fillAvailableHeight?: boolean;
  hasWarning?: boolean;
  hasError?: boolean;
}

export enum FeatureMode {
  Create = 'create',
  Edit = 'edit',
  Read = 'read',
  EditChange = 'editChange',
}

export interface FeatureEntity {
  id: string | number;
  name?: string;
  logo?: string;
  color?: string;
  status?: string;
  // Optional properties for different entity types
  avatarLink?: string; // Used by TeamMember
  info?: {
    // Used by entities with nested info (e.g., Project)
    name?: string;
    logoUrl?: string;
    status?: string;
  };
}

/**
 * Base interface that all tab form data must extend.
 * Defines the minimum structure required for form data.
 */
export interface BaseTabFormData {
  // All tab form data should be objects, but we don't enforce specific properties
  // This allows each tab to define its own form structure while maintaining type safety
}

/**
 * Generic tab reference interface that all feature tabs must implement
 */
export interface FeatureTabRef<TFormData extends BaseTabFormData = BaseTabFormData> {
  /** Submit handler that receives optional date range for conflict detection */
  submit: (dateRange?: SubmitDateRange) => Promise<void>;
  isValid: boolean;
  getFormData: () => TFormData;
  /** Whether the tab is currently checking for conflicts (exposed for loading state) */
  isCheckingConflicts?: boolean;
}

export interface ActionButtonConfig {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'destructive' | 'outlineDestructive';
  className?: string;
}

export interface ErrorAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'secondary' | 'destructive';
  icon?: ReactNode;
}

export interface FeatureDetailsProps<
  T extends FeatureEntity = FeatureEntity,
  TFormData extends BaseTabFormData = BaseTabFormData,
> {
  // Core data and state
  entityData?: T;
  mode: FeatureMode;
  currentTab: string;
  isLoading: boolean;
  isLoadingEntity: boolean;
  error?: Error | null;

  // Tabs configuration - unified approach
  tabs: FeatureTab<TFormData>[];

  // Navigation and routing
  entityIdParam?: string;
  getDraftData: (tab: string) => Partial<TFormData> | undefined;
  saveDraftData: (params: { tab: string; data: TFormData }) => void;
  clearDraft: () => void;
  getFinalSubmissionData: () => TFormData;
  hasDraftData: boolean;
  handleSave: (params: {
    data: TFormData;
    successCallback?: (result: PatchEntityResult) => void;
  }) => void;
  calculateHasAnyData?: (context: {
    mode: FeatureMode;
    currentTab: string;
    tabs: FeatureTab<TFormData>[];
    getDraftData: (tab: string) => Partial<TFormData> | undefined;
    hasDraftData: boolean;
  }) => boolean;

  // Actions
  onBack: () => void;
  onFormStateChange: (isDirty: boolean) => void;
  onTabChange: (tab: string, additionalParams?: Record<string, string>) => void;
  onRefetch?: () => void;

  // Unsaved changes confirmation (optional)
  enableUnsavedChangesConfirmation?: boolean;
  unsavedChangesConfig?: {
    title: string;
    message: string;
    confirmText: string;
    confirmVariant?: 'default' | 'destructive' | 'error';
  };

  // Callback to get the form state change handler for tab content
  onFormStateChangeHandlerReady?: (handler: (isDirty: boolean) => void) => void;

  /**
   * Callback that provides the wrapped tab change handler to parent component.
   * This handler includes unsaved changes protection. Tab content should use this
   * instead of calling the hook's handleTabChange directly.
   */
  onTabChangeHandlerReady?: (
    handler: (tab: string, additionalParams?: Record<string, string>) => void
  ) => void;

  /**
   * Callback that provides the allowNavigation function from unsaved changes guard.
   * Use this to bypass the guard after successful save operations.
   */
  onAllowNavigationReady?: (allowNavigation: () => void) => void;

  // Optional customizations
  pageTitle?: string;
  createTitle?: string;
  loadingMessage?: string;
  errorMessage?: string;

  // Entity-specific features
  showAvatar?: boolean;
  showStatus?: boolean;
  statusBadgeType: StatusBadgeType;
  avatarEditable?: boolean;
  onAvatarChange?: (file: File) => Promise<void> | void;
  onAvatarRemove?: () => void;
  isUploadingAvatar?: boolean;
  getStatusText?: (status: string) => string;

  // Action buttons (for edit mode)
  actionButtons?: ActionButtonConfig[];
  // Force action buttons into a single dropdown (3-dot menu) at all breakpoints.
  // Use when extra buttons in a specific status would overflow the header (e.g. Invited team member).
  forceActionButtonsDropdown?: boolean;

  // Footer customization - can provide custom footer or use built-in generic footer
  footerContent?: ReactNode;
  hideFooter?: boolean;

  // Generic footer configuration (alternative to footerContent)
  useGenericFooter?: boolean;
  footerProps?: Partial<FeatureFooterProps>;

  // Edit change mode specific
  disabledTabs?: string[];
  onCancelEditChange?: () => void;

  // Modals and overlays
  modals?: ReactNode;

  // Error handling
  errorActions?: ErrorAction[];

  // get tab form state to controll external stuff
  setTabValid?: (name: string, isValid: boolean) => void;

  patchResultToDisplay: PatchEntityResult | null;
}

export interface FeatureFooterProps {
  mode: FeatureMode;
  isLoading: boolean;
  hasFormChanges: boolean;
  isFormValid?: boolean;
  /** Submit handler - receives date range in Edit/EditChange modes */
  onSubmit: (dateRange?: SubmitDateRange) => void;

  // Translation function
  t: (key: string) => string;
  // Visibility control
  isVisible?: boolean;

  // Custom labels (optional - will use defaults if not provided)
  labels?: {
    saving?: string; // Button text while submitting (e.g., "Saving...")
    create?: string; // Submit button text in Create mode (e.g., "Create project")
    saveChanges?: string; // Submit button text in Edit mode (e.g., "Apply changes")
    dateOfChange?: string; // Label for single date picker in Create mode (e.g., "Date of Change*")
    dateRange?: string; // Label for date range picker in Edit/EditChange modes (e.g., "Date Range*")
    selectChangeDate?: string; // Placeholder for single date picker (e.g., "Select change date")
    selectDateRange?: string; // Placeholder for date range picker (e.g., "Select date range")
    cancel?: string; // Cancel button text (e.g., "Cancel")
    updateRecord?: string; // Submit button text in EditChange mode (e.g., "Update record")
  };

  // Date picker configuration (for edit mode)
  showDatePicker?: boolean;
  /** Use a single date picker instead of a date range picker */
  useSingleDatePicker?: boolean;
  /** Disable past dates in the single date picker */
  disablePastDates?: boolean;
  /** Disable the entire date picker (e.g., when only non-versioned fields changed) */
  isDatePickerDisabled?: boolean;
  /**
   * Minimum selectable date for the date picker. Dates before this are
   * non-selectable (e.g. a versioned change cannot start before the project
   * start date). Applies to both the single date picker and the range picker.
   */
  minDate?: Date;

  // EditChange mode specific
  onCancel?: () => void;
  selectedDate?: string;
  selectedEndDate?: string | null;
}
